import {useId, type ComponentType} from 'react';
import {Input} from '@/components/ui/input';
import {Textarea} from '@/components/ui/textarea';
import {Checkbox} from '@/components/ui/checkbox';
import {Switch} from '@/components/ui/switch';
import {Slider} from '@/components/ui/slider';
import {Label} from '@/components/ui/label';
import {ChoiceSelect, ChoiceMultiSelect} from '@/components/app/choice-select';
import {
  MaskedInput,
  NumberInput,
  PasswordInput,
  SegmentedControl,
  ToggleField,
  DatePicker,
} from '@/components/app/inputs';
import {cn} from '@/lib/utils';
import type {FieldPath, UseControllerReturn} from 'react-hook-form';
import type {FieldObjectWithType, FieldValues} from './types';
export interface FieldWithTypeProps<
  T extends FieldValues = FieldValues,
  N extends FieldPath<T> = FieldPath<T>,
> extends UseControllerReturn<T, N> {
  fieldObjectWithType: FieldObjectWithType<T, N>;
}
export function FieldWithType<T extends FieldValues, N extends FieldPath<T> = FieldPath<T>>({
  field,
  fieldState,
  fieldObjectWithType: object,
}: FieldWithTypeProps<T, N>) {
  const generatedId = useId();
  const options = object.typeOptions as Record<string, any> | undefined;
  const id = object.id ?? options?.inputId ?? options?.id ?? generatedId;
  const errorId = `${id}-error`;
  const {type, label, typeOptions, className, style, labelClassName, labelStyle, errorClassName, errorStyle} = object;
  const common = {
    id,
    name: field.name,
    disabled: field.disabled,
    onBlur: field.onBlur,
    'aria-invalid': !!fieldState.error,
    'aria-describedby': fieldState.error ? errorId : undefined,
  };
  // The union is discriminated by `type`; each branch forwards that control's options.
  const renderControl = () => {
    if (typeof type !== 'string') {
      const Custom = type as ComponentType<any>;
      return <Custom {...common} value={field.value} onValueChange={field.onChange} {...options} />;
    }
    if (type === 'checkbox')
      return (
        <Checkbox
          {...common}
          ref={field.ref}
          checked={!!field.value}
          onCheckedChange={(checked) => field.onChange(checked === true)}
          {...options}
        />
      );
    if (type === 'switch')
      return (
        <Switch {...common} ref={field.ref} checked={!!field.value} onCheckedChange={field.onChange} {...options} />
      );
    if (type === 'dropdown')
      return <ChoiceSelect {...common} value={field.value} onValueChange={field.onChange} {...options} />;
    if (type === 'multiselect')
      return <ChoiceMultiSelect {...common} value={field.value ?? []} onValueChange={field.onChange} {...options} />;
    if (type === 'number')
      return (
        <NumberInput {...common} ref={field.ref} value={field.value} onValueChange={field.onChange} {...options} />
      );
    if (type === 'mask')
      return (
        <MaskedInput
          {...common}
          ref={field.ref}
          mask={options?.mask ?? ''}
          value={field.value}
          onValueChange={field.onChange}
          {...options}
        />
      );
    if (type === 'password')
      return (
        <PasswordInput
          {...common}
          ref={field.ref}
          value={field.value ?? ''}
          onChange={(event) => field.onChange(event.target.value)}
          {...options}
        />
      );
    if (type === 'select-button')
      return (
        <SegmentedControl
          {...common}
          value={field.value}
          onValueChange={field.onChange}
          aria-label={typeof label === 'string' ? label : field.name}
          {...options}
        />
      );
    if (type === 'toggle')
      return <ToggleField {...common} checked={!!field.value} onCheckedChange={field.onChange} {...options} />;
    if (type === 'slider')
      return (
        <Slider
          {...common}
          ref={field.ref}
          value={Array.isArray(field.value) ? field.value : [field.value ?? 0]}
          onValueChange={(values) => field.onChange(Array.isArray(field.value) ? values : values[0])}
          {...options}
        />
      );
    if (type === 'date') return <DatePicker value={field.value} onValueChange={field.onChange} {...options} />;
    if (type === 'textarea')
      return (
        <Textarea
          {...common}
          ref={field.ref}
          value={field.value ?? ''}
          onChange={(event) => field.onChange(event.target.value)}
          {...options}
        />
      );
    return (
      <Input
        {...common}
        ref={field.ref}
        type={type === 'email' ? 'email' : 'text'}
        value={field.value ?? ''}
        onChange={(event) => field.onChange(event.target.value)}
        {...options}
      />
    );
  };
  const inline = type === 'checkbox';
  const text = typeof label === 'string' ? label : 'This field';
  const error = fieldState.error;
  const bounds: Record<string, string> = {
    max: 'larger than',
    min: 'smaller than',
    maxLength: 'longer than',
    minLength: 'shorter than',
  };
  let message = error?.message || (error?.type === 'required' ? `${text} is required` : `${text} is invalid`);
  if (error && !error.message && bounds[error.type]) {
    const bound = object.rules?.[error.type as 'max'];
    const limit = typeof bound === 'object' ? bound.value : bound;
    message = `${text} cannot be ${bounds[error.type]} ${limit}${error.type.endsWith('Length') ? ' characters' : ''}.`;
  }
  const labelElement =
    label == null ? null : (
      // Top labels and errors are indented to line up with the text inside pill-shaped inputs.
      <Label htmlFor={id} className={cn(!inline && 'pl-4', labelClassName)} style={labelStyle}>
        {label}
      </Label>
    );
  return (
    <div className={cn('flex min-w-0 flex-col gap-2', className)} style={style}>
      {inline ? (
        <div className="flex items-center gap-2">
          {renderControl()}
          {labelElement}
        </div>
      ) : (
        <>
          {labelElement}
          {renderControl()}
        </>
      )}
      {error && (
        <small
          id={errorId}
          role="alert"
          className={cn('pl-4 text-sm text-destructive', errorClassName)}
          style={errorStyle}
        >
          {message}
        </small>
      )}
    </div>
  );
}
